import cloneDeep from 'lodash/cloneDeep';
import { TravelType } from '@hotelplan/graphql.types';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { useSearchState } from '@hotelplan/libs.search-state';
import { calculateNextPage } from '@hotelplan/libs.utils';
import { mapOrlFilterValuesToPdpFilterCriteriaInput } from 'components/domain/filters/Filters.mappers';
import { mapSearchControlStateToPdpSearchCriteriaInput } from 'components/domain/orl/Orl.mappers';
import { IOrlState } from 'components/domain/orl/Orl.types';
import { useORLSortingContext } from 'components/domain/orl/orlResults/ORLSorting';
import usePDPSearchValues from 'components/domain/pdp/usePDPSearchValues';
import { OrlItemFragment } from 'graphql/fragments/ORLItem.generated';
import { InternalLinkFragmentFragment } from 'graphql/link/InternalLink.generated';
import {
  GetOrlSingleOffersQuery,
  GetOrlSingleOffersQueryVariables,
  useGetOrlSingleOffersQuery,
} from 'graphql/orl/GetORLSingleOffers.generated';

export const useGetORLSingleOffersVariables = (
  initialResultsPerPage: number,
  groupId?: string
): {
  loading: boolean;
  variables: GetOrlSingleOffersQueryVariables;
  orlState: IOrlState;
} => {
  const { id } = useObjectId();
  const { sortingValue } = useORLSortingContext();

  const { loading, state } = useSearchState<IOrlState>();

  const { filters, searchControl } = usePDPSearchValues();

  return {
    loading,
    variables: {
      pageNumber: 0,
      resultsPerPage: initialResultsPerPage,
      sort: sortingValue,
      id,
      searchControl:
        mapSearchControlStateToPdpSearchCriteriaInput(searchControl),
      filters: mapOrlFilterValuesToPdpFilterCriteriaInput(filters),
      groupId,
    },
    orlState: state,
  };
};

interface IUsePdpOffers {
  loading: boolean;
  offers: Array<OrlItemFragment>;
  status: string;
  onShowMore: (nextPage: number | null) => void;
  possibleNextPage: number;
  altData: {
    altTravelType: TravelType;
    altUrl: InternalLinkFragmentFragment | null;
  };
  totalResults?: number;
}

const usePdpOffers = (
  initialResultsPerPage: number,
  onCompleted?: (
    res: GetOrlSingleOffersQuery,
    queryTime: number,
    orlState: IOrlState
  ) => void
): IUsePdpOffers => {
  const now = Date.now();

  const {
    loading: stateLoading,
    variables,
    orlState,
  } = useGetORLSingleOffersVariables(initialResultsPerPage);

  const { data, previousData, loading, fetchMore } = useGetOrlSingleOffersQuery(
    {
      variables,
      ssr: false,
      skip: stateLoading,
      notifyOnNetworkStatusChange: true,
      onCompleted: orlSingleData => {
        const queryTime = Date.now() - now;
        onCompleted?.(orlSingleData, queryTime, orlState);
      },
    }
  );

  const fetchMoreOffers = (nextPage: number | null): void => {
    if (nextPage === null) return;

    fetchMore({
      variables: {
        ...variables,
        pageNumber: nextPage,
        resultsPerPage: 50,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const finalOffersData = cloneDeep(fetchMoreResult);
        finalOffersData.orl.searchResult.single.offers.unshift(
          ...prev.orl.searchResult.single.offers
        );
        return finalOffersData;
      },
    });
  };
  const result =
    data?.orl.searchResult.single || previousData?.orl.searchResult.single;

  const possibleNextPage = calculateNextPage(
    result?.page.pageNumber || 0,
    result?.page.resultsPerPage,
    result?.page.resultsTotal
  );

  return {
    loading,
    offers: result?.offers,
    status: result?.status,
    onShowMore: fetchMoreOffers,
    possibleNextPage,
    altData: {
      altTravelType: result?.altTravelType,
      altUrl: result?.alternativeSearchUrl,
    },
    totalResults: result?.page.resultsTotal,
  };
};

export default usePdpOffers;
